import React from 'react';
import type { ReportExportColumn } from '@core/utils/methods/report-export-table-data';
import {
  BPExpirationRow,
  OptionProps,
  PermittingOverviewRow,
  ReportKey,
  ReportsFilters,
  ReportsSortBy,
} from '@models/interfaces';
import { getTableSortOrder } from '../helpers';
import { renderValue } from './common';

export type PermittingRow = PermittingOverviewRow | BPExpirationRow;

export const formatPermittingDate = (value: unknown): string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return '--';

  return `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}`;
};

export const getPermittingColumns = (
  reportKey: ReportKey.PermittingOverview | ReportKey.BPExpiration,
  filters: ReportsFilters,
  sortOptions: OptionProps[],
  dateFields: OptionProps[],
): ReportExportColumn<PermittingRow>[] => {
  const sortable = (
    key: ReportsSortBy,
  ): Pick<ReportExportColumn<PermittingRow>, 'sorter' | 'sortOrder'> => ({
    sorter: sortOptions.some(option => option.value === key),
    sortOrder:
      filters.sortBy === key ? getTableSortOrder(filters.sortOrder) : undefined,
  });
  const columns: ReportExportColumn<PermittingRow>[] = [
    {
      key: 'siteNumber',
      dataIndex: 'siteNumber',
      title: 'Site Number',
      width: 160,
      render: renderValue,
      ...sortable(ReportsSortBy.SiteNumber),
    },
    {
      key: 'projectBundleID',
      dataIndex: 'projectBundleID',
      title: 'Project Bundle ID',
      width: 160,
      ...sortable(ReportsSortBy.ProjectBundleID),
      exportValue: row => row.projectBundleID,
      render: (_value: unknown, row: PermittingRow) => (
        <a
          href={`/permitting/${row.projectBundleID}/details?tabName=Workflow`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.projectBundleID}
        </a>
      ),
    },
    {
      key: 'paceProjectNumbersDisplay',
      dataIndex: 'paceProjectNumbersDisplay',
      title: 'PACE Project Numbers',
      width: 220,
      render: renderValue,
    },
    {
      key: 'oracleProjectNumbersDisplay',
      dataIndex: 'oracleProjectNumbersDisplay',
      title: 'Oracle Project Numbers',
      width: 220,
      render: renderValue,
    },
  ];

  if (reportKey === ReportKey.PermittingOverview) {
    return [
      ...columns,
      {
        key: 'jurisdictionName',
        dataIndex: 'jurisdictionName',
        title: 'Jurisdiction Name',
        width: 220,
        render: renderValue,
      },
      ...dateFields.map(
        ({ value, viewValue }): ReportExportColumn<PermittingRow> => ({
          key: String(value),
          dataIndex: String(value),
          title: String(viewValue),
          width: 180,
          render: formatPermittingDate,
          ...(value === ReportsSortBy.BPExpiration
            ? sortable(ReportsSortBy.BPExpiration)
            : {}),
        }),
      ),
    ];
  }

  return [
    ...columns,
    {
      key: 'BPExpiration',
      dataIndex: 'BPExpiration',
      title: 'BP Expiration Date',
      width: 180,
      render: formatPermittingDate,
      ...sortable(ReportsSortBy.BPExpiration),
    },
    {
      key: 'expirationStatus',
      title: 'Expiration Status',
      width: 280,
      exportValue: row => ('expirationLabel' in row ? row.expirationLabel : ''),
      render: (_value: unknown, row: PermittingRow) =>
        'expirationStatus' in row ? (
          <span
            className={`prov-permitting-report__status prov-permitting-report__status--${row.expirationStatus}`}
          >
            {row.expirationLabel}
          </span>
        ) : (
          '--'
        ),
    },
  ];
};
